import { Injectable, NgZone } from '@angular/core';
import { GlobalThemeService } from '@app/services/global-theme-service/global.theme.service';
import { ConfigService } from '@core/config.service';
import { LOGIN_URL } from '@app/shared/constants/app.constants';
import * as authConstants from './auth.constants';
import * as _ from 'lodash';
import {BehaviorSubject} from "rxjs";
import { authDebug, authDebugSessionSnapshot } from './auth-debug.util';
declare let DisneyID: any;
declare let PEP: any;
@Injectable()
export class AuthService {
    private didappid: string;
    private responderPage: string;
    private campus: string;
    private config: any;

    private _accessToken: string;
    private _swid: string;

    private isInitialized$ = new BehaviorSubject<boolean>(false);
    private reAuthInFlight: Promise<void> | null = null;

    constructor(
        private globalThemeService: GlobalThemeService,
        private configService: ConfigService,
        private ngZone: NgZone
    ) {
        this.campus = this.globalThemeService.getCampusBasedOnHost();
        this.config = _.cloneDeep(this.configService.config);
        this.didappid = this.campus === authConstants.dvc ? this.config.dvcClientId : this.config.aulaniClientId;
        this.responderPage = this.config.baseURL + 'assets/html/lightbox/' + this.config.env.toLowerCase() + '.html';
    }

    redirectToLightboxLogin(): Promise<any> {
        const that = this;
        return new Promise(resolve => {
            document.getElementsByTagName('head')[0].appendChild(this.generateLightBoxScript()).onload = () => {
                const did = DisneyID.get({
                    clientId: this.didappid,
                    responderPage: this.responderPage,
                    debug: false,
                    blueCookie: true,
                    cssOverride: 'https://secure.parksandresorts.wdpromedia.com/media/resorts/lightbox/custom-disid.css?1'
                });
                function loginSuccess() {
                    console.log('Login Successful.');
                    that.globalThemeService.getWindow().location.reload();
                }

                did.init()
                    .then(function initSuccess(data) {
                        if (data.loggedIn) {
                            // Already authenticated (e.g. return from IdP). Do not register `login` → reload
                            // or a full reload can run before Angular bootstraps and the shell stays blank.
                            resolve(data);
                        } else {
                            did.on('login', loginSuccess);
                            did.launchLogin();
                        }
                    })
                    .catch(function initError(data) {
                        console.log('User not logged in');
                        did.on('login', loginSuccess);
                        did.launchLogin();
                    });
            };
        });
    }

    redirectToWDWlogin(): void {
        const hostName =
            this.globalThemeService.getHostName() === 'localhost'
                ? 'latest.disneyworld.disney.go.com'
                : this.globalThemeService.getHostName();
        let returnUrl = this.globalThemeService
            .getWindow()
            .location.href.replace(hostName, '')
            .replace('https://', '');
        if (returnUrl.includes('/error/')) {
            returnUrl = returnUrl.replace('/error/', '/resort-reservation/');
        }
        const loginStub = LOGIN_URL;
        const url = `https://${hostName}${loginStub}?returnUrl=${returnUrl}`;
        this.globalThemeService.getWindow().location.href = url;
    }

    private generateLightBoxScript(): HTMLScriptElement {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = 'disneyid-script';
        script.charset = 'utf-8';
        script.async = true;
        script.src = this.config.lightboxDIDURL;

        return script;
    }

  public async reAuth(): Promise<void> {
      if (this.reAuthInFlight) {
          authDebug('reAuth: already in flight — awaiting existing call');
          return this.reAuthInFlight;
      }

      this.reAuthInFlight = this.runReAuth().finally(() => {
          this.reAuthInFlight = null;
      });
      return this.reAuthInFlight;
  }

  private async runReAuth(): Promise<void> {
      authDebug('reAuth: start');
      try {
        const guest = await PEP.PROFILE_AUTH.launchReauth();
        authDebug('reAuth: launchReauth succeeded', {
          hasNestedToken: !!guest?.token?.access_token,
          hasTopLevelToken: !!guest?.access_token
        });
        await this.applySessionFromPepPayload(guest, 'reAuth');
      } catch (e) {
        authDebug('reAuth: failed', { message: e?.message, e });
        console.error(e.message, e);
      }
  }

  async initialize(): Promise<void> {
    authDebug('initialize: start');
    authDebugSessionSnapshot('initialize: entry snapshot', this);
    // Register before launchLogin so a fast PEP login event is not missed.
    this.registerLoginEvents();
    await this.checkFirstLoggedInStatus();
    this.registerPostLoggedInEvents();
    authDebug('initialize: complete');
    authDebugSessionSnapshot('initialize: exit snapshot', this);
  }

  logout(): any {
    return PEP.PROFILE_AUTH.logout();
  }

  private async checkFirstLoggedInStatus () {
    authDebug('checkFirstLoggedInStatus: start');
    const loggedInStatus = await PEP.PROFILE_AUTH.getLoggedInStatus();
    authDebug('checkFirstLoggedInStatus: getLoggedInStatus', { loggedInStatus });
    if (!loggedInStatus) {
      try {
        authDebug('checkFirstLoggedInStatus: calling launchLogin');
        await PEP.PROFILE_AUTH.launchLogin();
        const loggedInAfterLogin = await PEP.PROFILE_AUTH.getLoggedInStatus();
        authDebug('checkFirstLoggedInStatus: after launchLogin', { loggedInAfterLogin });
        if (loggedInAfterLogin) {
          await this.checkTrustStatus();
        } else {
          authDebug('checkFirstLoggedInStatus: still not logged in after launchLogin');
        }
      } catch (err) {
        authDebug('checkFirstLoggedInStatus: launchLogin error', { err });
        console.info('PEP launchLogin did not complete', err);
      }
    } else {
      authDebug('checkFirstLoggedInStatus: already logged in — checkTrustStatus');
      await this.checkTrustStatus();
    }
  }

  private registerLoginEvents(): void {
    authDebug('registerLoginEvents: attaching PEP login/reauth handlers');
    const actions = (payload: unknown, source: string) => {
      const token = this.extractSessionToken(payload);
      authDebug(`registerLoginEvents: ${source} event received`, {
        hasToken: !!token,
        hasAccessToken: !!token?.access_token,
        hasSwid: !!token?.swid
      });
      if (!token?.access_token) {
        authDebug(`registerLoginEvents: ${source} event skipped — no access_token`);
        return;
      }
      this.setLoggedInGuestData(token, source);
      this.registerPostLoggedInEvents();
    };
    PEP.PROFILE_AUTH.on('login', (guest) =>{
      actions(guest, 'login');
    });
    PEP.PROFILE_AUTH.on('reauth', (guest) =>{
      actions(guest, 'reauth');
    });
  }

  private async checkTrustStatus() {
    authDebug('checkTrustStatus: start');
    const highTrustStatus = await PEP.PROFILE_AUTH.getTrustStateStatus();
    authDebug('checkTrustStatus: getTrustStateStatus', { highTrustStatus });
      if (highTrustStatus) {
         await this.refresh();
      } else {
         authDebug('checkTrustStatus: low trust — reAuth');
         await this.reAuth();
      }
  }

  private registerPostLoggedInEvents (): void {
    authDebug('registerPostLoggedInEvents: attaching PEP post-login handlers');
    PEP.PROFILE_AUTH.on('low-trust', () =>{
      authDebug('PEP low-trust event');
      this.reAuth().then(() => {
        authDebug('PEP low-trust reAuth succeeded');
        console.info("low-trust event on PEP");
      }).catch((err)=> {
        authDebug('PEP low-trust reAuth failed — reloading', { err });
        location.reload();
      });
    });
    PEP.PROFILE_AUTH.on('refresh', (payload) =>{
      authDebug('PEP refresh event');
      this.setLoggedInGuestData(this.extractSessionToken(payload), 'refresh');
    });

    //add logout,close,refresh
  }

  private async refresh(): Promise<void> {
     await this.applySessionFromPepPayload(await PEP.PROFILE_AUTH.getGuest(), 'refresh-getGuest');
  }

  /**
   * Apply PEP session credentials when present on the payload; otherwise read the
   * updated session via getGuest(). launchReauth() on WDW often resolves without a
   * bearer even after the guest completes the password modal.
   */
  private async applySessionFromPepPayload(payload: unknown, source: string): Promise<void> {
    const token = this.extractSessionToken(payload);
    if (token?.access_token) {
      this.setLoggedInGuestData(token, source);
      return;
    }

    authDebug(`applySessionFromPepPayload: no token on ${source} payload — calling getGuest`, { source });
    const guest = await PEP.PROFILE_AUTH.getGuest();
    authDebug('applySessionFromPepPayload: getGuest succeeded', { source });
    this.setLoggedInGuestData(this.extractSessionToken(guest), `${source}-getGuest`);
  }

  /**
   * PEP may return session credentials nested under `token` (guest wrapper) or at the
   * top level (same shape as the `refresh` event). Normalize before applying session state.
   */
  private extractSessionToken(payload: unknown): { access_token?: string; swid?: string } | null {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    const record = payload as {
      token?: { access_token?: string; swid?: string };
      access_token?: string;
      swid?: string;
      data?: { token?: { access_token?: string; swid?: string } };
      oneIDGuest?: { token?: { access_token?: string; swid?: string } };
      guest?: { token?: { access_token?: string; swid?: string } };
    };

    const nestedCandidates = [
      record.token,
      record.data?.token,
      record.oneIDGuest?.token,
      record.guest?.token
    ];
    for (const candidate of nestedCandidates) {
      if (candidate?.access_token) {
        return candidate;
      }
    }

    if (record.access_token) {
      return { access_token: record.access_token, swid: record.swid };
    }
    return null;
  }

  private setLoggedInGuestData (token: any, source = 'unknown') {
    if (!token?.access_token) {
      authDebug('setLoggedInGuestData: skipped — no access_token', { source });
      return;
    }

    const apply = (): void => {
      authDebug('setLoggedInGuestData', {
        source,
        inAngularZone: NgZone.isInAngularZone(),
        hasToken: !!token,
        hasAccessToken: !!token?.access_token,
        accessTokenLength: token?.access_token?.length ?? 0,
        hasSwid: !!token?.swid
      });
      this._accessToken = token.access_token;
      this._swid = token.swid;
      this.isInitialized$.next(true);
      authDebug('setLoggedInGuestData: isInitialized set to true', { source });
    };

    if (NgZone.isInAngularZone()) {
      apply();
    } else {
      authDebug('setLoggedInGuestData: PEP callback outside Angular zone — re-entering via NgZone.run', { source });
      this.ngZone.run(apply);
    }
  }

  /**
   * After Disney ID lightbox or mobile cookie bootstrap, session tokens are known but PEP
   * `initialize()` was never run. HttpClient must see `isInitialized` true or requests stall.
   */
  applyGuestSessionFromLightboxToken(token: { access_token: string; swid: string }): void {
    authDebug('applyGuestSessionFromLightboxToken', {
      hasAccessToken: !!token?.access_token,
      accessTokenLength: token?.access_token?.length ?? 0,
      hasSwid: !!token?.swid
    });
    this.setLoggedInGuestData(token, 'lightbox-or-mobile-cookie');
  }

  isUnauthorizedError(err): Boolean {
    const UNAUTHORIZED = 401,
      FORBIDDEN = 403;
    return err.status &&
      (err.status === UNAUTHORIZED || err.status === FORBIDDEN);
  }

  get isInitialized(): BehaviorSubject<boolean> {
    return this.isInitialized$;
  }

  get swid(): string {
    return this._swid;
  }

  get accessToken(): string {
    return this._accessToken;
  }

  set swid(newSwid) {
    this._swid = newSwid;
  }

  set accessToken(newToken) {
    this._accessToken = newToken;
  }
}
